import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Displays a confirmation modal informing the user that an order has been successfully cancelled and showing the refunded amount, with actions to close the modal or initiate a re-booking.
 * @component
 * @selector app-order-cancellation-success-modal
 * @example
 *   <app-order-cancellation-success-modal
 *     [isOpen]="true"
 *     [refundAmount]="'$12.34'"
 *     [isScheduledOrder]="false"
 *     (close)="handleClose()"
 *     (bookAgain)="handleBookAgain()"
 *   ></app-order-cancellation-success-modal>
 * @input boolean isOpen - Controls whether the modal is visible; defaults to false.
 * @input string refundAmount - Formatted refund amount to display to the user; defaults to an empty string.
 * @input boolean isScheduledOrder - Indicates if the cancelled order was a scheduled order; may alter displayed messaging.
 * @output EventEmitter<void> close - Emitted when the user closes the modal (no payload).
 * @output EventEmitter<void> bookAgain - Emitted when the user chooses to book again after cancellation (no payload).
 * @method onClose(): void - Handles user intent to close the modal by emitting the close event to parent components.
 * @method onBookAgain(): void - Handles the "book again" action by emitting the bookAgain event to parent components.
 * @lifecycle This component does not implement Angular lifecycle hooks (no ngOnInit/ngOnChanges required for its behavior).
 * @file src/app/components/order-cancellation-success-modal/order-cancellation-success-modal.component.ts
 */
@Component({
  selector: 'app-order-cancellation-success-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './order-cancellation-success-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./order-cancellation-success-modal.component.scss'],
})
export class OrderCancellationSuccessModalComponent {
  @Input() isOpen = false;
  @Input() refundAmount = '';
  @Input() isScheduledOrder = false;
  @Output() close = new EventEmitter<void>();
  @Output() bookAgain = new EventEmitter<void>();

  onClose(): void {
    this.close.emit();
  }

  onBookAgain(): void {
    this.bookAgain.emit();
  }
}
